/**
 * GroupListView — "By Group" view for document permissions.
 *
 * Shows workspace groups, expandable to see which docs each group
 * has access to with editable roles.
 */
import {
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  RowInput,
  Scrollable,
} from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useDocMetaHelper } from '@affine/core/components/hooks/use-block-suite-page-meta';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { DocMutationService } from '@affine/core/modules/doc';
import { objectRoleLabel } from '@affine/core/modules/permissions/utils/object-role-labels';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DocRole,
  getGroupDocRolesQuery,
  getGroupsQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  DeleteIcon,
  PageIcon,
  PlusIcon,
  SearchIcon,
} from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../../shared/settings-offline-status';
import {
  SettingsEntityCard,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import * as entityStyles from '../shared/settings-entity.css';
import { SettingsGroupSummary } from '../shared/settings-identity';
import * as styles from './styles.css';

interface GroupInfo {
  id: string;
  name: string;
  description?: string | null;
}

interface GroupDocRole {
  id: string;
  docId: string;
  role: string;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

interface GroupListViewProps {
  searchQuery: string;
}

const DOC_GRANTABLE_ROLES = [
  DocRole.Manager,
  DocRole.Editor,
  DocRole.Commenter,
  DocRole.Reader,
];

const getAreaBlockedNotice = (
  t: ReturnType<typeof useI18n>,
  entry: GroupDocRole
) => {
  if (!entry.isAreaBlocked) {
    return null;
  }
  const areaName = entry.areaBoundaryRootName;
  if (entry.areaBoundaryKind === 'private' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.private']({ areaName });
  }
  if (entry.areaBoundaryKind === 'teamspace' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.teamspace']({
      areaName,
    });
  }
  return t['com.streamlain.folder-share.area-blocked.generic']();
};

export const GroupListView = ({ searchQuery }: GroupListViewProps) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspaceService.workspace.id;

  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!backendAvailable || !serverService.server) {
        setIsLoading(false);
        return;
      }
      try {
        const result = await serverService.server.gql({
          query: getGroupsQuery,
          variables: { workspaceId },
        });
        setGroups(
          (result.groups ?? []).map(
            (g: { id: string; name: string; description?: string | null }) => ({
              id: g.id,
              name: g.name,
              description: g.description,
            })
          )
        );
      } catch (err) {
        console.debug('Failed to load groups:', err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [backendAvailable, serverService.server, workspaceId]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase();
    return groups.filter(g => g.name.toLowerCase().includes(q));
  }, [groups, searchQuery]);

  const handleGroupClick = useCallback((groupId: string) => {
    setExpandedGroupId(prev => (prev === groupId ? null : groupId));
  }, []);

  if (isLoading && backendAvailable) {
    return (
      <SettingsEntityMessage>
        <Loading size={20} />
        <span>
          {t['com.streamlain.settings.workspace.permissions.loading']?.() ??
            'Loading...'}
        </span>
      </SettingsEntityMessage>
    );
  }

  if (!backendAvailable && groups.length === 0) {
    return <SettingsEntityMessage>{offlineLabel}</SettingsEntityMessage>;
  }

  if (filteredGroups.length === 0) {
    return (
      <SettingsEntityMessage>
        {searchQuery
          ? (t[
              'com.streamlain.settings.workspace.permissions.no-groups-found'
            ]?.() ?? 'No groups found.')
          : (t[
              'com.streamlain.settings.workspace.permissions.no-groups-found'
            ]?.() ?? 'No groups in this workspace.')}
      </SettingsEntityMessage>
    );
  }

  return (
    <div>
      {filteredGroups.map(group => (
        <GroupListItem
          key={group.id}
          group={group}
          isExpanded={expandedGroupId === group.id}
          onClick={() => handleGroupClick(group.id)}
        />
      ))}
    </div>
  );
};

// =============================================================================
// Group List Item (expandable)
// =============================================================================

const GroupListItem = ({
  group,
  isExpanded,
  onClick,
}: {
  group: GroupInfo;
  isExpanded: boolean;
  onClick: () => void;
}) => {
  return (
    <SettingsEntityCard>
      <SettingsGroupSummary
        expanded={isExpanded}
        onToggle={onClick}
        group={group}
      />
      {isExpanded && (
        <div className={entityStyles.expanded}>
          <GroupDocsList groupId={group.id} groupName={group.name} />
        </div>
      )}
    </SettingsEntityCard>
  );
};

// =============================================================================
// Group Docs List (shows docs a group has access to, with editable roles)
// =============================================================================

const GroupDocsList = ({
  groupId,
  groupName: _groupName,
}: {
  groupId: string;
  groupName: string;
}) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const docMutationService = useService(DocMutationService);
  const docCollection = workspaceService.workspace.docCollection;
  const workspaceId = workspaceService.workspace.id;
  const { getDocMeta } = useDocMetaHelper();

  const [docRoles, setDocRoles] = useState<GroupDocRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Add state
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<DocRole>(DocRole.Editor);

  const loadDocRoles = useCallback(async () => {
    if (!serverService.server) {
      setIsLoading(false);
      return;
    }
    try {
      const result = await serverService.server.gql({
        query: getGroupDocRolesQuery,
        variables: { workspaceId, groupId },
      });
      setDocRoles(
        (result.groupDocRoles ?? []).map(r => ({
          id: r.id,
          docId: r.docId,
          role: r.role,
          isAreaBlocked: r.isAreaBlocked,
          areaBoundaryKind: r.areaBoundaryKind,
          areaBoundaryRootFolderId: r.areaBoundaryRootFolderId,
          areaBoundaryRootName: r.areaBoundaryRootName,
        }))
      );
    } catch (err) {
      console.debug('Failed to load group doc roles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [serverService.server, workspaceId, groupId]);

  useEffect(() => {
    void loadDocRoles();
  }, [loadDocRoles]);

  // Available docs to add
  const availableDocs = useMemo(() => {
    if (!showAdd) return [];
    const grantedIds = new Set(docRoles.map(r => r.docId));
    const docs: { id: string; title: string }[] = [];
    docCollection.docs.forEach((_doc, id) => {
      if (grantedIds.has(id)) return;
      const meta = getDocMeta(id);
      if (meta && !meta.trash) {
        docs.push({ id, title: meta.title || 'Untitled' });
      }
    });
    const q = addSearch.toLowerCase();
    return q ? docs.filter(d => d.title.toLowerCase().includes(q)) : docs;
  }, [showAdd, docRoles, docCollection.docs, getDocMeta, addSearch]);

  const handleGrant = useCallback(
    async (docId: string) => {
      setIsUpdating(true);
      try {
        await docMutationService.grantGroupRole({
          docId,
          groupId,
          role: selectedRole,
        });
        setDocRoles(prev => [
          ...prev.filter(role => role.docId !== docId),
          {
            id: `pending:${groupId}:${docId}`,
            docId,
            role: selectedRole,
          },
        ]);
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.granted']?.() ??
            'Permission granted',
        });
        setShowAdd(false);
        setAddSearch('');
      } catch (err) {
        console.error('Failed to grant group doc access:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.grant-failed'
            ]?.() ?? 'Failed to grant permission',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [docMutationService, groupId, selectedRole, t]
  );

  const handleUpdateRole = useCallback(
    async (docId: string, newRole: string) => {
      setIsUpdating(true);
      try {
        await docMutationService.grantGroupRole({
          docId,
          groupId,
          role: newRole,
        });
        setDocRoles(prev =>
          prev.map(role =>
            role.docId === docId ? { ...role, role: newRole } : role
          )
        );
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.updated']?.() ??
            'Permission updated',
        });
      } catch (err) {
        console.error('Failed to update group doc role:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.update-failed'
            ]?.() ?? 'Failed to update permission',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [docMutationService, groupId, t]
  );

  const handleRevoke = useCallback(
    async (docId: string) => {
      setIsUpdating(true);
      try {
        await docMutationService.revokeGroupRole({
          docId,
          groupId,
        });
        setDocRoles(prev => prev.filter(role => role.docId !== docId));
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.revoked']?.() ??
            'Access removed',
        });
      } catch (err) {
        console.error('Failed to revoke group doc role:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.revoke-failed'
            ]?.() ?? 'Failed to remove access',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [docMutationService, groupId, t]
  );

  const getRoleLabel = useCallback(
    (role: string) => objectRoleLabel(t, role),
    [t]
  );

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Loading size={16} />
      </div>
    );
  }

  return (
    <div className={styles.editorContainer}>
      {/* Header with Add button */}
      <div className={styles.editorHeader}>
        <span className={styles.editorLabel}>
          {t[
            'com.streamlain.settings.workspace.permissions.user-doc-access'
          ]?.() ?? 'Document access'}
        </span>
        <Button
          variant="plain"
          prefix={<PlusIcon />}
          onClick={() => setShowAdd(!showAdd)}
          disabled={isUpdating}
        >
          {t['com.streamlain.settings.workspace.permissions.add-user']?.() ??
            'Add'}
        </Button>
      </div>

      {/* Add doc picker */}
      {showAdd && (
        <>
          <div className={styles.inviteContainer}>
            <div className={styles.inviteSearch}>
              <SearchIcon className={styles.searchIcon} />
              <RowInput
                value={addSearch}
                onChange={setAddSearch}
                placeholder={
                  t[
                    'com.streamlain.settings.workspace.permissions.search-docs'
                  ]?.() ?? 'Search documents...'
                }
                className={styles.searchInputField}
              />
            </div>
            <Menu
              items={DOC_GRANTABLE_ROLES.map(role => (
                <MenuItem
                  key={role}
                  selected={role === selectedRole}
                  onSelect={() => setSelectedRole(role)}
                >
                  {getRoleLabel(role)}
                </MenuItem>
              ))}
              contentOptions={{ align: 'end' }}
            >
              <MenuTrigger className={styles.ownerMenuTrigger}>
                {getRoleLabel(selectedRole)}
              </MenuTrigger>
            </Menu>
          </div>

          {availableDocs.length > 0 && (
            <Scrollable.Root className={styles.inviteMembersList}>
              <Scrollable.Viewport>
                {availableDocs.slice(0, 20).map(doc => (
                  <div
                    key={doc.id}
                    className={styles.inviteMemberRow}
                    onClick={() => {
                      void handleGrant(doc.id);
                    }}
                  >
                    <PageIcon
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{doc.title}</div>
                    </div>
                  </div>
                ))}
              </Scrollable.Viewport>
              <Scrollable.Scrollbar />
            </Scrollable.Root>
          )}

          {availableDocs.length === 0 && addSearch && (
            <div className={styles.noMembersFound}>
              {t[
                'com.streamlain.settings.workspace.permissions.no-docs-found'
              ]?.() ?? 'No documents found'}
            </div>
          )}
        </>
      )}

      {/* Current grants */}
      {docRoles.length === 0 ? (
        <div className={styles.emptyPermissions}>
          {t[
            'com.streamlain.settings.workspace.permissions.no-granted-users'
          ]?.() ?? 'No document permissions for this group.'}
        </div>
      ) : (
        <div className={styles.grantedUsersList}>
          {docRoles.map(dr => {
            const meta = getDocMeta(dr.docId);
            const title = meta?.title || 'Untitled';
            const areaBlockedNotice = getAreaBlockedNotice(t, dr);

            return (
              <div
                key={dr.docId}
                className={styles.permissionRowWrapper}
                data-area-blocked={dr.isAreaBlocked ? 'true' : undefined}
              >
                <div
                  className={`${styles.permissionRow} ${
                    dr.isAreaBlocked ? styles.areaBlockedPermissionRow : ''
                  }`}
                >
                  <div className={styles.permissionUser}>
                    <PageIcon
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{title}</div>
                    </div>
                  </div>

                  <Menu
                    items={DOC_GRANTABLE_ROLES.map(role => (
                      <MenuItem
                        key={role}
                        selected={role === dr.role}
                        onSelect={() => {
                          void handleUpdateRole(dr.docId, role);
                        }}
                      >
                        {getRoleLabel(role)}
                      </MenuItem>
                    ))}
                    contentOptions={{ align: 'end' }}
                  >
                    <MenuTrigger
                      className={styles.ownerMenuTrigger}
                      disabled={isUpdating}
                    >
                      {getRoleLabel(dr.role)}
                    </MenuTrigger>
                  </Menu>

                  <Button
                    variant="plain"
                    className={styles.removeButton}
                    onClick={() => {
                      void handleRevoke(dr.docId);
                    }}
                    disabled={isUpdating}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
                {areaBlockedNotice ? (
                  <div className={styles.areaBlockedNotice}>
                    {areaBlockedNotice}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
